import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/back-button';
import { Pencil, Sparkles } from 'lucide-react';
import { contactListService } from '@/lib/services/contact-lists';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/page-header';
import type { Contact } from '@/lib/services/contacts';
import useSWR from 'swr';
import type { ContactListDetail as ContactListDetailType } from '@/lib/services/contact-lists';
import { ContactsTable, ToolsButton } from '@/components/contacts/ContactsTable';
import { PageShell, PageTopRow } from '@/components/ui/page-shell';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

export default function ContactListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const toolsToggleRef = useRef<(() => void) | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isAutoProspecting, setIsAutoProspecting] = useState(false);

  const queryParams = new URLSearchParams({ page: String(page), limit: '50' });
  if (searchQuery) queryParams.set('search', searchQuery);
  const { data: listData, isLoading, mutate: mutateList } = useSWR<ContactListDetailType>(
    listId ? `/api/contact-lists/${listId}?${queryParams}` : null,
    fetcher
  );

  const contacts = (listData?.contacts || []) as Contact[];
  const pagination = listData?.pagination;

  const [listName, setListName] = useState('');

  useEffect(() => {
    if (listData?.name && !listName) setListName(listData.name);
  }, [listData]);

  useEffect(() => {
    if (!listId || !listName.trim() || !listData || listName === listData.name) return;
    const timeout = setTimeout(() => {
      contactListService.updateList(parseInt(listId), { name: listName }).catch(() => toast.error('Failed to update list name'));
    }, 800);
    return () => clearTimeout(timeout);
  }, [listName, listId, listData]);

  useEffect(() => {
    if (searchInput.length === 0) {
      if (searchQuery) { setSearchQuery(''); setPage(1); }
      return;
    }
    if (searchInput.length < 3) return;
    const timer = setTimeout(() => { setSearchQuery(searchInput); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const deleteContact = async (id: number) => {
    try {
      await fetch(`/api/contacts/${id}`, { method: 'DELETE', credentials: 'include' });
      mutateList();
      return true;
    } catch { return false; }
  };
  const bulkDeleteContacts = async (ids: number[]) => {
    try {
      await fetch('/api/contacts', { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) });
      mutateList();
      return true;
    } catch { return false; }
  };
  const runAutoProspect = async () => {
    if (!listId || isAutoProspecting) return;
    setIsAutoProspecting(true);
    try {
      const result = await contactListService.autoProspect(parseInt(listId), 25);
      if (result.exhausted && result.fetched === 0) {
        toast.info('No more prospects match the current ICP — change a filter to find more.');
      } else if (result.fetched === 0) {
        toast.info('Apollo returned no rows for the current filters. Try adjusting the workspace ICP.');
      } else if (result.added === 0) {
        toast.info(`Fetched ${result.fetched} prospects but they were already in this list.`);
      } else if (result.exhausted) {
        toast.success(`Added ${result.added} prospect${result.added === 1 ? '' : 's'} — that's the last batch matching this ICP.`);
      } else {
        toast.success(`Added ${result.added} prospect${result.added === 1 ? '' : 's'} to the list.`);
      }
      mutateList();
    } catch (err) {
      toast.error(`Auto Prospect failed: ${(err as Error).message}`);
    } finally {
      setIsAutoProspecting(false);
    }
  };

  const updateContact = async (id: number, field: string, value: string) => {
    try {
      await fetch(`/api/contacts/${id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
      mutateList(
        (current) => current && {
          ...current,
          contacts: current.contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
        },
        { revalidate: false }
      );
      return true;
    } catch { return false; }
  };

  if (!listData && !isLoading) {
    return (
      <div className="px-gutter pt-page-top pb-gutter">
        <PageHeader title="List not found" backHref="/dashboard/lists" backTooltip="Back to Lists" />
      </div>
    );
  }

  return (
    <PageShell className="overflow-hidden">
      <PageTopRow className="gap-4">
        <BackButton href="/dashboard/lists" tooltip="Back to Lists" />
        <div className="relative">
          <Input
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            className="text-lg font-medium focus:ring-0 pl-4 pr-10 w-[20rem] h-12 rounded-full !mb-0 truncate"
            placeholder="List name"
          />
          <Pencil className="h-4 w-4 absolute right-4 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none" />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Input
            type="text"
            placeholder="Search contacts"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-sm w-52"
            prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          />
          <Button
            type="button"
            variant="primary"
            onClick={runAutoProspect}
            disabled={isAutoProspecting}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {isAutoProspecting ? 'Prospecting…' : 'Auto Prospect'}
          </Button>
          <ToolsButton onClick={() => toolsToggleRef.current?.()} hasEnabled={false} />
        </div>
      </PageTopRow>

      <div className="flex-1 min-h-0 flex flex-col -mx-gutter">
        <ContactsTable
          contacts={contacts}
          isLoading={isLoading}
          deleteContact={deleteContact}
          bulkDelete={bulkDeleteContacts}
          updateContact={updateContact}
          pagination={pagination}
          onPageChange={setPage}
          searchQuery={searchQuery}
          onClearSearch={() => { setSearchInput(''); setSearchQuery(''); setPage(1); }}
          emptyMessage="No contacts in this list yet. Push contacts from Find Leads to add them."
          toolsButtonRef={toolsToggleRef}
        />
      </div>
    </PageShell>
  );
}
